import { useContext, useEffect } from 'react';
import ReactGA from 'react-ga';
import { ResizeObserver } from 'resize-observer';

import { UserContext } from '@incentivegames/ig-frontend-common/lib/contexts/userContext';
import { communicationUtilities } from '@incentivegames/ig-frontend-common/lib/utilities/communicationUtilities';

import MainController from './controllers/MainController/MainController';
import { devTools } from './common/devtools';
import { useDefaultLanguage } from './hooks/language/language';
import { useDefaultCurrency } from './hooks/currency/currency';
import i18n from './theme/i18n';
import config from './theme/config';
import { useConfigStore } from './store/options/optionsStore';
import './shared.scss';
import './App.scss';
import { useShallow } from 'zustand/react/shallow';

interface IAppProps {}

const App = (props: IAppProps) => {
  devTools.log('Using site id:', process.env.REACT_APP_SITE_ID);

  const userContext = useContext(UserContext);
  const devPredictorId = devTools.getPredictorIdViaUrl();
  const oddsType = new URLSearchParams(document.location.search).get(config.settings.oddsTypeIdentifier);
  const setOddsType = useConfigStore((s) => s.setOddsType);

  const url = new URL(window.location.href);
  const countryCode = url.searchParams.get(config.settings.countryCodeIdentifier);
  const stateCode = url.searchParams.get(config.settings.countryRegionCodeIdentifier);
  const gameTitle = url.searchParams.get(config.settings.gameTitleIdentifier);
  const [setGameTitle, setCountryCode, setCountryRegion] = useConfigStore(
    useShallow((s) => [s.setGameTitle, s.setCountryCode, s.setCountryRegion]),
  );

  useDefaultLanguage();
  useDefaultCurrency();

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      communicationUtilities.sendPostMessage.resize();
    });

    resizeObserver.observe(document.body);

    ReactGA.initialize(process.env.REACT_APP_GA_TRACKING_CODE || '', { debug: false });
    ReactGA.pageview(window.location.pathname + window.location.search);

    userContext.user.addConfigHandlers(
      (token: string) => userContext.setToken(token),
      (language: string) => {
        userContext.setLanguage(language);
        i18n.changeLanguage(language);
      },
      (currency: string) => userContext.setCurrency(currency),
    );

    /**
     * Prevents scrolling if the game content exceeds the window size.
     * This does not affect environments with iframes because we resize the observer.
     */
    if (process.env.REACT_APP_SITE_ENV !== 'dev') {
      document.body.style.overflow = 'hidden';
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const handleUpdatePriceType = (e: MessageEvent): void => {
      if (e.data.action === 'updatePriceType') {
        setOddsType(e.data.payload.priceType);
      }
    };
    window.addEventListener('message', handleUpdatePriceType, false);
    return () => {
      window.removeEventListener('message', handleUpdatePriceType, false);
    };
  }, [setOddsType]);

  useEffect(() => {
    setOddsType(config.settings.getUserOddsPreference(oddsType));
  }, [oddsType, setOddsType]);

  useEffect(() => {
    setGameTitle(gameTitle || undefined);
  }, [gameTitle, setGameTitle]);

  useEffect(() => {
    setCountryCode(countryCode || undefined);
  }, [countryCode, setCountryCode]);

  useEffect(() => {
    setCountryRegion(stateCode || undefined);
  }, [stateCode, setCountryRegion]);

  return (
    <div className='app'>
      <MainController predictorId={devPredictorId} />
    </div>
  );
};

export default App;
